import LogoSVG from '@public/icons/logo.svg';
import MenuLinesSVG from '@public/icons/menu-lines.svg';
import classNames from 'classnames';
import React, { useEffect } from 'react';
import { useFullLogoStateValue } from '@/shared/atoms/full-logo';
import { useHeaderAnchorsValue } from '@/shared/atoms/header-anchors';
import { useHeaderThemeValue, useSetHeaderTheme } from '@/shared/atoms/header-theme';
import { useSetOpenedPopupsState } from '@/shared/atoms/opened-popups';
import { MENU_POPUP_NAME } from '@/shared/components/popups/MenuPopup/MenuPopup';
import FeedbackBtn from '@/shared/components/shared/FeedbackBtn';
import HeaderDropdown from '@/shared/components/shared/HeaderDropdown';
import Link from '@/shared/components/shared/Link';
import NavigationAnchors from '@/shared/components/shared/NavigationAnchors';
import { EVENTS_NAV } from '@/shared/consts/events-nav';
import { LIVE_NAV } from '@/shared/consts/live-nav';
import { PARTICIPATION_NAV_ITEMS, PARTICIPATION_NAV_TITLE } from '@/shared/consts/participation-nav';
import { Theme } from '@/shared/lib/types';
import { SEARCH_POPUP_NAME } from '../../popups/SearchPopup/SearchPopup';

const themes: Theme[] = ['light', 'dark', 'light-bg'];

type HeaderNavItem = {
    name: string;
    slug?: string;
    links?: { name: string; slug: string }[];
    onClick?: () => void;
};

const Header = () => {
    const defaultTheme: Theme = 'light';
    const theme = useHeaderThemeValue();
    const setHeaderTheme = useSetHeaderTheme();
    const headerAnchors = useHeaderAnchorsValue();
    const { openPopup } = useSetOpenedPopupsState();

    const isFullLogo = useFullLogoStateValue();
    const centerNav: HeaderNavItem[] = [
        {
            name: 'FORUM',
            slug: '/participation/participants/',
        },
        {
            name: 'EXPO',
            slug: '/participation/visitors/',
        },
        LIVE_NAV,
        {
            name: 'Программа',
            slug: '/program/',
        },
        {
            name: 'Партнеры',
            slug: '/participation/partners/',
        },
        {
            name: 'Выставка',
            slug: '/expo/',
        },
        {
            name: 'Новости',
            slug: '/news/',
        },
        // EVENTS_NAV,
        // {
        //     name: PARTICIPATION_NAV_TITLE,
        //     links: PARTICIPATION_NAV_ITEMS,
        // },

        {
            name: 'Контакты',
            slug: '/contacts/',
        },
    ];

    useEffect(() => {
        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    const target = entry.target as HTMLElement;

                    if (entry.isIntersecting) {
                        const theme = target.dataset.theme as Theme;
                        setHeaderTheme(themes.includes(theme) ? theme : defaultTheme);
                    }
                });
            },
            { rootMargin: '-6% 0% -93.5% 0%', threshold: [0, 1] },
        );

        let timeout: NodeJS.Timeout;

        const onNewPageReady = () => {
            observer.disconnect();
            clearTimeout(timeout);
            timeout = setTimeout(() => {
                Array.from(document.querySelectorAll<HTMLElement>('.js-header-theme-trigger')).forEach((el) => {
                    observer.observe(el);
                });
            }, 100);
        };

        onNewPageReady();
        document.addEventListener('new-page-ready', onNewPageReady);

        return () => {
            clearTimeout(timeout);
            document.removeEventListener('new-page-ready', onNewPageReady);
            observer.disconnect();
        };
    }, [setHeaderTheme]);

    return (
        <header className={`header header-theme--${theme}`}>
            <div className="wrapper header-wrapper">
                <div className="header-left">
                    <Link href={'/'} className="header-logo" aria-label="Главная">
                        <LogoSVG className={classNames('header-logo-svg', { 'full-logo': isFullLogo })} />
                    </Link>
                    <button
                        className="header-menu header-btn blur-bg middle-out"
                        onClick={() => {
                            openPopup(MENU_POPUP_NAME);
                            window._txq?.push(['track', 'menu']);
                        }}
                    >
                        <div className="menu-btn-text text-xs">Меню</div>
                        <MenuLinesSVG className="header-lines-svg" />
                    </button>
                </div>
                <div className="header-center blur-bg">
                    {centerNav.map((link, i) =>
                        link.links ? (
                            <HeaderDropdown links={link.links} btnText={link.name} key={i} onClick={link.onClick} />
                        ) : (
                            <Link href={link.slug!} key={i} prefetch className="header-btn middle-out text-xs">
                                {link.name}
                            </Link>
                        ),
                    )}
                </div>

                {/* <div className="header-search">
                    <button
                        className="header-btn blur-bg middle-out text-xs"
                        onClick={() => {
                            openPopup(SEARCH_POPUP_NAME);
                            window._txq?.push(['track', 'poisk']);
                        }}
                    >
                        Поиск
                    </button>
                </div> */}
                <div className="header-right">
                    <Link
                        href="https://lk.oilgasforum.ru/auth/"
                        target="_self"
                        className="header-btn blur-bg middle-out text-xs"
                        onClick={() => {
                            window._txq?.push(['track', 'LK']);
                        }}
                    >
                        <span>Личный кабинет</span>
                    </Link>

                    <Link
                        target="_self"
                        href="/registration/"
                        className="header-btn blur-bg text-xs primary-header-btn"
                        onClick={() => {
                            //@ts-expect-error
                            window.runRapPixel?.('ab42eebb-dfb0-45f6-aab4-d33891023609');
                            window._txq?.push(['track', 'prinyat_uchastie']);
                        }}
                    >
                        Регистрация
                    </Link>
                </div>
            </div>
            {headerAnchors && (
                <NavigationAnchors
                    embedded
                    theme={theme}
                    anchors={headerAnchors.anchors}
                    navigation={headerAnchors.navigation}
                    scrollYProgress={headerAnchors.scrollYProgress}
                    {...(headerAnchors.universal ? { universal: true } : {})}
                />
            )}
            <FeedbackBtn theme={theme} className={classNames({ 'is-hide': !isFullLogo })} />
        </header>
    );
};

export default Header;
